import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Sparkles, Crown, Trophy, ArrowRight } from "lucide-react";
import MotionButton from "./motion/MotionButton";
import useDailyQuiz from "../hooks/useDailyQuiz";
import {
  DailyHeader, DailyOptions, ExplanationBlock,
  LoadingState, SecondaryCTA, VerifyButton,
} from "./DailyQuestionsParts";

const DAILY_QUESTION_COUNT = 3;
const PASS_PCT = 67;

const goToLogin = () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS, THIS BREAKS AUTH
  const redirectUrl = window.location.origin + "/auth/callback";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

function FeedbackBlock({ q, selected, isAuthenticated, isLast, onNext, onUnlock }) {
  const correct = selected === q.correct_index;
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="mt-6"
    >
      <div
        data-testid="daily-result-text"
        className={`text-sm font-semibold ${correct ? "text-brand-green" : "text-brand-red"}`}
      >
        {correct ? "✓ Risposta corretta" : "✗ Risposta sbagliata"}
        <span className="text-brand-secondary ml-2 font-normal">· {q.specialty}</span>
      </div>
      <ExplanationBlock
        explanation={q.explanation}
        isAuthenticated={isAuthenticated}
        onUnlock={onUnlock}
      />
      <div className="mt-5 flex flex-col sm:flex-row gap-3">
        {isLast ? (
          <MotionButton data-testid="daily-finish-btn" variant="red" onClick={onNext}>
            <Sparkles className="h-4 w-4" /> Vedi il risultato finale
          </MotionButton>
        ) : (
          <MotionButton data-testid="daily-next-btn" variant="primary" onClick={onNext}>
            Prossima domanda <ArrowRight className="h-4 w-4" />
          </MotionButton>
        )}
      </div>
    </motion.div>
  );
}

function FinalScreen({ score, total, isAuthenticated, onPrimary }) {
  const percent = total > 0 ? Math.round((score / total) * 100) : 0;
  const isGood = percent >= PASS_PCT;
  const c = isGood
    ? { border: "border-brand-green/40", bg: "bg-brand-green/5", text: "text-brand-green" }
    : { border: "border-brand-red/40",   bg: "bg-brand-red/5",   text: "text-brand-red"   };
  const headline = isGood
    ? "Bel lavoro. Pronto a fare lo step successivo?"
    : "Hai trovato i tuoi punti deboli. Lavoriamoci insieme.";

  return (
    <motion.div
      initial={{ scale: 0.96, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ duration: 0.5 }}
      className={`border-2 rounded-2xl p-7 sm:p-9 text-center ${c.border} ${c.bg}`}
    >
      <Trophy className={`h-12 w-12 mx-auto mb-3 ${c.text}`} />
      <div className="eyebrow">// Risultato di oggi</div>
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", stiffness: 200, damping: 18, delay: 0.25 }}
        data-testid="daily-final-score"
        className={`heading-display text-7xl sm:text-8xl mt-3 ${c.text}`}
      >
        {score}/{total}
      </motion.div>
      <h3 className="font-serif text-2xl sm:text-3xl text-brand-primary mt-4">{headline}</h3>
      <p className="text-base text-brand-secondary mt-3 max-w-md mx-auto leading-relaxed">
        Le 3 domande del giorno sono solo un assaggio. Sblocca <span className="text-brand-primary font-medium">2.000+ domande</span>,
        simulazioni cronometrate e Tutor AI per arrivare preparato all'esame.
      </p>
      <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
        <MotionButton data-testid="daily-cta-unlock-btn" variant="red" size="lg" onClick={onPrimary}>
          <Crown className="h-4 w-4" />
          {isAuthenticated ? "Sblocca Premium" : "Continua con Google"}
        </MotionButton>
      </div>
      <p className="text-xs text-brand-secondary mt-4">
        Torna domani: avrai 3 nuove domande pronte alle 00:00 UTC.
      </p>
    </motion.div>
  );
}

function PlayingCard({ daily, idx, selected, revealed, score, isAuthenticated, setSelected, onVerify, onAdvance, onUnlock }) {
  const q = daily.questions[idx];
  const isLast = idx === daily.questions.length - 1;

  return (
    <motion.div
      key={`q-${idx}`}
      initial={{ opacity: 0, x: 16 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -16 }}
      transition={{ duration: 0.35 }}
      className="premium-card p-6 sm:p-8"
    >
      <DailyHeader date={daily.date} qIdx={idx} total={daily.questions.length} scoreSoFar={score} />

      <div className="flex flex-wrap gap-2 mb-3">
        <span className="px-2.5 py-1 rounded-md bg-brand-green/10 text-brand-green eyebrow text-[10px]">
          {q.specialty}
        </span>
        <span className="px-2.5 py-1 rounded-md bg-brand-surfaceElevated text-brand-secondary eyebrow text-[10px]">
          {q.topic}
        </span>
      </div>

      <p data-testid="daily-stem" className="font-serif text-xl sm:text-2xl text-brand-primary leading-relaxed">
        {q.stem}
      </p>

      <DailyOptions q={q} selected={selected} setSelected={setSelected} revealed={revealed} />

      {revealed ? (
        <FeedbackBlock
          q={q}
          selected={selected}
          isAuthenticated={isAuthenticated}
          isLast={isLast}
          onNext={onAdvance}
          onUnlock={onUnlock}
        />
      ) : (
        <VerifyButton disabled={selected === null} onClick={onVerify} />
      )}
    </motion.div>
  );
}

function SectionShell({ children }) {
  return (
    <section
      id="daily"
      data-testid="daily-questions-section"
      className="bg-brand-surfaceElevated border-y border-brand-border"
    >
      <div className="max-w-3xl mx-auto px-6 sm:px-12 py-20 sm:py-24">
        <div className="text-center mb-10">
          <div className="eyebrow text-brand-green mb-3">// Mini-simulazione · Senza registrazione</div>
          <h2 className="heading-display text-4xl sm:text-5xl text-brand-primary">
            Le <em className="italic-display text-brand-green">3 domande</em> del giorno
          </h2>
          <p className="text-base text-brand-secondary mt-4 max-w-xl mx-auto leading-relaxed">
            Ogni giorno selezioniamo 3 domande rappresentative dell'esame. Mettiti alla prova
            ora — risultato immediato, spiegazione subito dopo.
          </p>
        </div>
        {children}
      </div>
    </section>
  );
}

export default function DailyQuestions() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const {
    phase, error, daily, idx, selected, revealed, score,
    setSelected, verify, next,
  } = useDailyQuiz();

  const onPrimaryCTA = () => (user ? navigate("/premium") : goToLogin());

  if (error) {
    return (
      <SectionShell>
        <div className="text-center text-brand-secondary py-10">{error}</div>
      </SectionShell>
    );
  }

  if (phase === "loading" || !daily) {
    return <SectionShell><LoadingState /></SectionShell>;
  }

  return (
    <SectionShell>
      <AnimatePresence mode="wait">
        {phase === "playing" && (
          <PlayingCard
            daily={daily}
            idx={idx}
            selected={selected}
            revealed={revealed}
            score={score}
            isAuthenticated={!!user}
            setSelected={setSelected}
            onVerify={verify}
            onAdvance={next}
            onUnlock={onPrimaryCTA}
          />
        )}
        {phase === "done" && (
          <motion.div key="done" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <FinalScreen
              score={score}
              total={daily.questions.length}
              isAuthenticated={!!user}
              onPrimary={onPrimaryCTA}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {phase === "playing" && <SecondaryCTA user={user} onClick={onPrimaryCTA} />}
    </SectionShell>
  );
}

export { DAILY_QUESTION_COUNT };
